import { Store } from "@reduxjs/toolkit";

import {
  AuthProviderType,
  BaseAuthSetupOptions,
  KeycloakSetupOptions,
  OAuth2CapacitorSetupOptions,
  DeviceAuthSetupOptions,
} from "./interfaces/IAuthProvider";
import { AuthProviderFactory } from "./factory/AuthProviderFactory";
import {
  DeviceAuthProvider,
  DeviceAuthService,
} from "./providers/DeviceAuthProvider";
import { KeycloakProvider } from "./providers/KeycloakProvider";
import { OAuth2CapacitorProvider } from "./providers/OAuth2CapacitorProvider";

// Base options shared by all providers
interface BaseManagerOptions {
  configKey?: string;
}

// Keycloak provider options - explicitly select Keycloak
export interface KeycloakManagerOptions extends BaseManagerOptions {
  provider: "keycloak";
  silentCheckSsoRedirectUri?: string;
  onLoad?: "login-required" | "check-sso";
}

// OAuth2 Capacitor provider options - explicitly select OAuth2 for native apps
export interface OAuth2CapacitorManagerOptions extends BaseManagerOptions {
  provider: "oauth2-capacitor";
  appDomain?: string;
  loginRequire?: boolean;
}

// Device auth provider options - explicitly select device auth for TV/QR code login
export interface DeviceAuthManagerOptions extends BaseManagerOptions {
  provider: "device-auth";
  qrCodeRedirectUrl?: string;
}

// Union of all manager options - provider is required
export type AuthManagerSetupOptions =
  | KeycloakManagerOptions
  | OAuth2CapacitorManagerOptions
  | DeviceAuthManagerOptions;

type Provider = KeycloakProvider | OAuth2CapacitorProvider | DeviceAuthProvider;

export class AuthManager {
  private static instance: AuthManager;
  private provider: Provider | null = null;
  private providerType: AuthProviderType | null = null;
  private configKey = "VITE_KC_AUTH_CONFIG";

  private constructor() {}

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  async setup(
    store: Store,
    options: AuthManagerSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.configKey = options.configKey || "VITE_KC_AUTH_CONFIG";
    this.providerType = options.provider;

    const config = AuthProviderFactory.getConfigFromEnv(this.configKey);
    this.provider = AuthProviderFactory.createProvider(
      this.providerType,
      config,
    ) as Provider;

    const baseOptions: BaseAuthSetupOptions = { store };

    switch (options.provider) {
      case "keycloak": {
        const keycloakOptions: KeycloakSetupOptions = {
          ...baseOptions,
          silentCheckSsoRedirectUri: options.silentCheckSsoRedirectUri,
          onLoad: options.onLoad,
        };
        return (this.provider as KeycloakProvider).setup(keycloakOptions);
      }
      case "oauth2-capacitor": {
        const oauth2Options: OAuth2CapacitorSetupOptions = {
          ...baseOptions,
          appDomain: options.appDomain,
          loginRequire: options.loginRequire,
        };
        return (this.provider as OAuth2CapacitorProvider).setup(oauth2Options);
      }
      case "device-auth": {
        const deviceOptions: DeviceAuthSetupOptions = {
          ...baseOptions,
          qrCodeRedirectUrl: options.qrCodeRedirectUrl,
        };
        return (this.provider as DeviceAuthProvider).setup(deviceOptions);
      }
    }
  }

  async login(redirectUri?: string): Promise<void> {
    return this.provider?.login(redirectUri);
  }

  async logout(ignoreDispatch?: boolean): Promise<void> {
    return this.provider?.logout(ignoreDispatch);
  }

  async getToken(): Promise<string | undefined> {
    return this.provider?.getToken();
  }

  getCurrentToken(): string | undefined {
    return this.provider?.getCurrentToken();
  }

  getKcClientId(): string | undefined {
    return this.provider?.getClientId();
  }

  isInternalAccount(): boolean {
    return this.provider?.isInternalAccount() ?? false;
  }

  getProviderType(): AuthProviderType | null {
    return this.providerType;
  }

  getDeviceAuthService(): DeviceAuthService | null {
    if (this.provider instanceof DeviceAuthProvider) {
      return this.provider.getDeviceAuthService();
    }
    return null;
  }
}
